import { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import PageShell from "../components/PageShell";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import CampaignForm from "../components/campaign/CampaignForm";
import CampaignTable from "../components/campaign/CampaignTable";
import CampaignLog from "../components/campaign/CampaignLog";
import {
  useCampaigns,
  useTriggerTypes,
  useCampaignLog,
  useCampaignStats,
  useCampaignSql,
  useCreateCampaign,
  useUpdateCampaign,
  useDeleteCampaign,
  useRunCampaign,
  useTestCampaign,
} from "../features/super/hooks/useCampaigns";
import toast from "react-hot-toast";

export default function CampaignDashboard() {
  const { t } = useTranslation();

  // Data via tanstack
  const { data: campaigns = [] } = useCampaigns();
  const { data: triggerTypes = [] } = useTriggerTypes();
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();
  const deleteMutation = useDeleteCampaign();
  const runMutation = useRunCampaign();
  const testMutation = useTestCampaign();

  // UI state
  const [selectedId, setSelectedId] = useState(null);
  const [log, setLog] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Create form state
  const [createMode, setCreateMode] = useState(false);
  const [newTriggerType, setNewTriggerType] = useState("");
  const [newName, setNewName] = useState("");
  const [newConfig, setNewConfig] = useState({});

  // Edit form state
  const [editConfig, setEditConfig] = useState({});
  const [editEnabled, setEditEnabled] = useState(false);
  const [editName, setEditName] = useState("");

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Test campaign dialog
  const [testTarget, setTestTarget] = useState(null);
  const [testPhone, setTestPhone] = useState("");

  const fieldsFor = (triggerType) =>
    triggerTypes.find((tt) => tt.triggerType === triggerType)?.configFields ||
    [];

  const placeholdersFor = (triggerType) =>
    triggerTypes.find((tt) => tt.triggerType === triggerType)?.placeholders ||
    [];

  // Sync edit state when selection or campaigns change
  useEffect(() => {
    if (!selectedId) {
      setEditConfig({});
      setLog(null);
      return;
    }
    const campaign = campaigns.find((c) => c.id === selectedId);
    if (campaign) {
      setEditConfig(campaign.config || {});
      setEditEnabled(campaign.enabled);
      setEditName(campaign.name || "");
    }
  }, [selectedId, campaigns]);

  // Handlers
  const handleCreate = async () => {
    if (!newTriggerType) {
      toast.error(t("campaign.selectTrigger"));
      return;
    }
    try {
      await createMutation.mutateAsync({
        trigger_type: newTriggerType,
        config: newConfig,
        name: newName,
      });
      toast.success(t("campaign.toastCreated"));
      setCreateMode(false);
      setNewTriggerType("");
      setNewName("");
      setNewConfig({});
    } catch {
      toast.error(t("campaign.toastCreateFailed"));
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedId,
        data: { config: editConfig, enabled: editEnabled, name: editName },
      });
      toast.success(t("campaign.toastUpdated"));
    } catch {
      toast.error(t("campaign.toastUpdateFailed"));
    }
  };

  const withBusy = async (id, action) => {
    setBusyId(id);
    try {
      await action();
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = (id, enabled) =>
    withBusy(id, async () => {
      const campaign = campaigns.find((c) => c.id === id);
      if (!campaign) return;
      try {
        await updateMutation.mutateAsync({
          id,
          data: { config: campaign.config, enabled, name: campaign.name },
        });
      } catch {
        toast.error(t("campaign.toastToggleFailed"));
      }
    });

  const handleRun = (id) =>
    withBusy(id, async () => {
      try {
        const result = await runMutation.mutateAsync(id);
        toast.success(
          t("campaign.toastRunResult", {
            sent: result.totalSent,
            candidates: result.totalCandidates,
          }),
        );
      } catch {
        toast.error(t("campaign.toastRunFailed"));
      }
    });

  const handleTest = (id) => {
    setTestTarget(id);
    setTestPhone("");
  };

  const handleTestSend = async () => {
    if (!testTarget || !testPhone.trim()) return;
    try {
      const result = await testMutation.mutateAsync({
        id: testTarget,
        phone: testPhone.trim(),
      });
      toast.success(
        t("campaign.toastTestResult", { name: result.customerName }),
      );
      setTestTarget(null);
      setTestPhone("");
    } catch {
      toast.error(t("campaign.toastTestFailed"));
    }
  };

  const handleDelete = () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    return withBusy(id, async () => {
      try {
        await deleteMutation.mutateAsync(id);
        if (selectedId === id) setSelectedId(null);
        toast.success(t("campaign.toastDeleted"));
      } catch {
        toast.error(t("campaign.toastDeleteFailed"));
      }
    });
  };

  // Always fetch log when a campaign is selected
  const { data: logData } = useCampaignLog(selectedId);
  const { data: statsData } = useCampaignStats(selectedId);
  const { data: sqlData } = useCampaignSql(selectedId);

  const handleCopySql = async () => {
    if (!sqlData?.sql) return;
    try {
      await navigator.clipboard.writeText(sqlData.sql);
      toast.success(t("campaign.sqlCopied"));
    } catch {
      toast.error(t("campaign.sqlCopyFailed"));
    }
  };

  useEffect(() => {
    if (logData) setLog(logData);
  }, [logData]);

  const getLabel = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.label || triggerType;
  };

  const selectedCampaign = campaigns.find((c) => c.id === selectedId);
  const deleteTargetName =
    deleteTarget != null
      ? campaigns.find((c) => c.id === deleteTarget)?.name || `#${deleteTarget}`
      : "";

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <PageShell title={t("page.marketing")}>
      <div className="flex flex-col gap-4 h-full">
        {/* ── Campaign List ── */}
        <div className="w-full shrink-0">
          <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl">
                {t("campaign.count", { count: campaigns.length })}
              </h2>
              <Button size="sm" onClick={() => setCreateMode(!createMode)}>
                {createMode ? t("common.cancel") : t("campaign.create")}
              </Button>
            </div>

            {/* Create form */}
            {createMode && (
              <div className="border border-c-gray-400/30 rounded-lg p-4 space-y-3">
                <div>
                  <Label>{t("campaign.name")}</Label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t("campaign.namePlaceholder")}
                  />
                </div>
                <div>
                  <Label>{t("campaign.triggerType")}</Label>
                  <Select
                    value={newTriggerType}
                    onChange={(e) => {
                      setNewTriggerType(e.target.value);
                      setNewConfig({});
                    }}
                  >
                    <option value="">—</option>
                    {triggerTypes.map((tt) => (
                      <option key={tt.triggerType} value={tt.triggerType}>
                        {tt.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {newTriggerType && (
                  <CampaignForm
                    fields={fieldsFor(newTriggerType)}
                    config={newConfig}
                    onChange={setNewConfig}
                    disabled={saving}
                    triggerType={newTriggerType}
                    placeholders={placeholdersFor(newTriggerType)}
                  />
                )}
                <Button onClick={handleCreate} disabled={saving}>
                  {t("campaign.createBtn")}
                </Button>
              </div>
            )}

            <CampaignTable
              campaigns={campaigns}
              triggerTypes={triggerTypes}
              selectedId={selectedId}
              busyId={busyId}
              onSelect={setSelectedId}
              onToggle={handleToggle}
              onRun={handleRun}
              onTest={handleTest}
              onDelete={(id) => setDeleteTarget(id)}
            />
          </div>
        </div>

        {/* ── Edit Panel ── */}
        {selectedId && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl">
                  {editName ||
                    t("campaign.campaignFallback", { id: selectedId })}
                  {" — "}
                  {getLabel(selectedCampaign?.triggerType)}
                </h2>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {t("common.save")}
                </Button>
              </div>

              <div>
                <Label>{t("campaign.name")}</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder={t("campaign.namePlaceholder")}
                />
              </div>

              <CampaignForm
                fields={fieldsFor(selectedCampaign?.triggerType)}
                config={editConfig}
                onChange={setEditConfig}
                disabled={saving}
                triggerType={selectedCampaign?.triggerType}
                placeholders={placeholdersFor(selectedCampaign?.triggerType)}
              />
            </div>
          </div>
        )}

        {/* ── Stats Panel ── */}
        {selectedId && statsData && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl">{t("campaign.stats")}</h2>
                {sqlData?.sql && (
                  <Button size="sm" variant="secondary" onClick={handleCopySql}>
                    {t("campaign.copySql")}
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">
                    {statsData.candidates}
                  </div>
                  <div className="text-c-gray-400 text-sm">
                    {t("campaign.statCandidates")}
                  </div>
                </div>
                <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {statsData.sent}
                  </div>
                  <div className="text-c-gray-400 text-sm">
                    {t("campaign.statSent")}
                  </div>
                </div>
                <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {statsData.failed}
                  </div>
                  <div className="text-c-gray-400 text-sm">
                    {t("campaign.statFailed")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Log Panel ── */}
        {selectedId && log && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4 space-y-2">
              <h2 className="text-xl">{t("campaign.log")}</h2>
              <CampaignLog log={log} />
            </div>
          </div>
        )}
      </div>

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t("campaign.confirmDeleteTitle")}
        itemName={deleteTargetName}
        message={t("campaign.confirmDeleteMessage", { item: deleteTargetName })}
        confirmText={t("common.delete")}
        onConfirm={handleDelete}
      />

      {/* ── Test campaign dialog ── */}
      <ConfirmDialog
        open={testTarget != null}
        onOpenChange={(open) => {
          if (!open) {
            setTestTarget(null);
            setTestPhone("");
          }
        }}
        title={t("campaign.testDialogTitle")}
        message={t("campaign.testDialogDesc")}
        customContent={
          <div className="space-y-2">
            <Label>{t("campaign.testDialogPhone")}</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder={t("campaign.testDialogPhonePlaceholder")}
              disabled={testMutation.isPending}
            />
          </div>
        }
        confirmText={t("campaign.testDialogSend")}
        cancelText={t("campaign.testDialogCancel")}
        confirmVariant="secondary"
        onConfirm={handleTestSend}
        disabled={testMutation.isPending}
      />
    </PageShell>
  );
}
